import { useEffect, useRef, useState } from "react";
import { X, Info, AlertTriangle, AlertOctagon } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Site-wide announcement banner. Reads from `GET /api/announcements`
 * (admin → Announcements admin). Each announcement has a priority
 * (info / warning / urgent) which controls the colour scheme. Visitors
 * can dismiss; a dismissal sticks per-announcement-id in localStorage so
 * we don't keep showing the same one over and over.
 */
const TONE = {
  info: {
    bg: "bg-navy text-cream",
    accent: "text-gold",
    Icon: Info,
  },
  warning: {
    bg: "bg-gold text-navy",
    accent: "text-navy/85",
    Icon: AlertTriangle,
  },
  urgent: {
    bg: "bg-rose-700 text-cream",
    accent: "text-rose-100",
    Icon: AlertOctagon,
  },
};

const STORAGE_KEY = "fic_dismissed_announcements_v1";

function loadDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export default function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/announcements")
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setItems(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Publish the banner's current height as a CSS variable so the page
  // layout (Header, main padding) can offset itself dynamically.
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) {
      root.style.setProperty("--fic-banner-h", "0px");
      return;
    }
    const update = () => {
      root.style.setProperty("--fic-banner-h", `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty("--fic-banner-h", "0px");
    };
  });

  const visible = items.filter((a) => a.is_active !== false && !dismissed.has(a.id));
  if (visible.length === 0) return null;

  // Show only the top (most recent) active announcement to avoid stacking
  // multiple bars and pushing the hero out of view.
  const ann = visible[0];
  const tone = TONE[ann.priority] || TONE.info;

  const dismiss = () => {
    const next = new Set(dismissed);
    next.add(ann.id);
    setDismissed(next);
    saveDismissed(next);
  };

  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      data-testid="announcement-banner"
      className={`fixed top-0 left-0 right-0 z-[60] w-full ${tone.bg} text-sm shadow-md`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 pr-12 flex items-start gap-3">
        <tone.Icon size={18} className={`shrink-0 mt-0.5 ${tone.accent}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-snug" data-testid="announcement-title">
            {ann.title}
          </div>
          {ann.body && (
            <div className={`leading-snug mt-0.5 ${tone.accent}`}>
              {ann.body}
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss announcement"
          data-testid="announcement-dismiss"
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-black/10 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
